/**
 * useResizablePanel — Manages a resizable panel (the Inspector drawer).
 *
 * Provides a drag handle, min/max constraints, and localStorage persistence.
 * Auto-compacts to minWidth on screens ≤ 1280px.
 */
import { useState, useCallback, useRef, useEffect } from 'react';

interface UseResizablePanelOptions {
    /** localStorage key for persisting width */
    storageKey: string;
    /** Minimum width in pixels */
    minWidth: number;
    /** Maximum width in pixels */
    maxWidth: number;
    /** Default width in pixels */
    defaultWidth: number;
    /** Screen width threshold below which we auto-compact to minWidth */
    compactBreakpoint?: number;
}

interface UseResizablePanelReturn {
    width: number;
    isResizing: boolean;
    handleMouseDown: (e: React.MouseEvent) => void;
}

export function useResizablePanel({
    storageKey,
    minWidth,
    maxWidth,
    defaultWidth,
    compactBreakpoint = 1280,
}: UseResizablePanelOptions): UseResizablePanelReturn {
    const [width, setWidth] = useState(() => {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
            const parsed = parseInt(stored, 10);
            if (!isNaN(parsed) && parsed >= minWidth && parsed <= maxWidth) return parsed;
        }
        return defaultWidth;
    });

    const [isResizing, setIsResizing] = useState(false);
    const startXRef = useRef(0);
    const startWidthRef = useRef(0);

    // Auto-compact on small screens
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= compactBreakpoint) {
                setWidth(minWidth);
            }
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [compactBreakpoint, minWidth]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        startXRef.current = e.clientX;
        startWidthRef.current = width;
        setIsResizing(true);

        const handleMouseMove = (moveEvt: MouseEvent) => {
            // Inspector is on the right, so dragging LEFT should increase width
            const delta = startXRef.current - moveEvt.clientX;
            const newWidth = Math.min(maxWidth, Math.max(minWidth, startWidthRef.current + delta));
            setWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            // Persist
            const finalWidth = Math.min(maxWidth, Math.max(minWidth, startWidthRef.current + (startXRef.current - (window.event as any)?.clientX || 0)));
            localStorage.setItem(storageKey, String(finalWidth));
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [width, minWidth, maxWidth, storageKey]);

    return { width, isResizing, handleMouseDown };
}
